import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Plus, Pencil, Wallet, Coins, EyeOff, Eye, Trash2, AlertTriangle, Lock, TrendingUp, Scale, MessageSquare } from "lucide-react";
import {
    useInstanceConfig, useCurrencies, useAccounts,
    useCreateCurrency, useUpdateCurrency,
    useCreateAccount, useUpdateAccount, useDeleteAccount,
    useSetGoalValuationMode,
    useSetImbalanceTrustFrom,   // SPEC-050
} from "@/api/queries";
import { Modal } from "@/components/Modal";
import { ErrorState } from "@/components/ErrorState";
import { cn } from "@/lib/utils";
import type { CurrencyRow, AccountUsage } from "@/api/types";

/**
 * Настройки инстанса: валюты и вёдра (SPEC-053).
 *
 * До этой страницы завести счёт или валюту можно было только сырым SQL по
 * продовой D1 — свежий инстанс оставался заперт в наборе вёдер из миграции 0006.
 */

type Tab = "accounts" | "currencies";

const FORM_LABEL: Record<string, string> = { digital: "безнал", cash: "нал", external: "внешний" };

export function SettingsPage() {
    const [tab, setTab] = useState<Tab>("accounts");
    const config = useInstanceConfig();

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-3xl font-semibold tracking-tight">Настройки</h1>
                <p className="text-muted-foreground mt-1">
                    Оценка целевых фондов, валюты и вёдра инстанса. Ведро — пара «валюта × форма»:
                    место хранения денег, а не человек.
                </p>
            </div>

            {config.data && (
                <div className="rounded-xl border bg-card p-4 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm">
                    <div className="flex items-center gap-2">
                        <Lock className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="text-muted-foreground">Базовая валюта:</span>
                        <span className="font-medium">{config.data.base_currency}</span>
                        {config.data.base_currency_locked && (
                            <span className="text-xs text-muted-foreground">
                                — зашита в расчёты, параметром не меняется
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="text-muted-foreground">Разделы:</span>
                        {Object.entries(config.data.features).map(([key, on]) => (
                            <span
                                key={key}
                                className={cn(
                                    "px-2 py-0.5 rounded-md text-xs",
                                    on ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : "bg-secondary text-muted-foreground",
                                )}
                            >
                                {FEATURE_LABEL[key] ?? key}: {on ? "вкл" : "выкл"}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            <GoalValuationSection />
            <ImbalanceTrustSection />

            <NoteCleanupSection />

            <div className="grid grid-cols-2 gap-1 p-1 bg-secondary/60 rounded-xl max-w-xs">
                {([["accounts", "Вёдра"], ["currencies", "Валюты"]] as const).map(([value, label]) => (
                    <button
                        key={value}
                        onClick={() => setTab(value)}
                        className={cn(
                            "px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                            tab === value ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground",
                        )}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {tab === "accounts" ? <AccountsSection /> : <CurrenciesSection />}
        </div>
    );
}

const FEATURE_LABEL: Record<string, string> = {
    investments: "Инвестиции",
    adaptive_budgets: "Адаптивные лимиты",
    coach: "Подсказки",
};

// ── Оценка целевых фондов (SPEC-054) ────────────────────────────────────────

/**
 * Тумблер режима оценки. Меняет цифры сразу на всех денежных экранах, поэтому
 * подпись объясняет ОБА состояния: пользователь должен понимать, что именно он
 * переключает, до клика, а не по изменившемуся балансу после.
 */
/**
 * SPEC-055: вход в разовую чистку комментариев. Отдельной страницей, а не секцией
 * прямо здесь: ревью 20+ пар — работа на несколько минут, ей нужен свой экран.
 */
function NoteCleanupSection() {
    return (
        <div className="rounded-xl border bg-card p-4">
            <div className="flex items-start justify-between gap-6">
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <MessageSquare className="h-4 w-4 text-muted-foreground shrink-0" />
                        <h2 className="font-medium">Комментарии к тратам</h2>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl">
                        Найти опечатки, разный регистр и одно и то же слово двумя раскладками — и слить
                        их к одному написанию. Ничего не применяется без подтверждения, каждая правка откатывается.
                    </p>
                </div>
                <Link to="/settings/note-cleanup" className="btn-primary shrink-0">Открыть</Link>
            </div>
        </div>
    );
}

function GoalValuationSection() {
    const config = useInstanceConfig();
    const setMode = useSetGoalValuationMode();
    const on = config.data?.goal_funds_mark_to_market ?? true;
    const busy = setMode.isPending || config.isLoading;

    return (
        <div className="rounded-xl border bg-card p-4">
            <div className="flex items-start justify-between gap-6">
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-muted-foreground shrink-0" />
                        <h2 className="font-medium">Оценка целевых фондов</h2>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl">
                        {on
                            ? "Включено: вложенное в цель считается по текущему курсу валюты вклада — фонд показывает, сколько отложенное стоит сегодня."
                            : "Выключено: каждый вклад зафиксирован по курсу на дату вклада — фонд не переоценивается вслед за курсом."}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1.5 max-w-2xl">
                        Действует во всём приложении: цели, «Целевые фонды» и «Свободные» на дашборде и «Счетах».
                        Записи не пересчитываются — меняется только правило отображения.
                    </p>
                    {setMode.isError && (
                        <p className="text-sm text-destructive mt-2">Не удалось переключить — попробуй ещё раз.</p>
                    )}
                </div>
                <button
                    type="button"
                    role="switch"
                    aria-checked={on}
                    aria-label="Оценивать целевые фонды по текущему курсу"
                    disabled={busy}
                    onClick={() => setMode.mutate(!on)}
                    className={cn(
                        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                        on ? "bg-emerald-500" : "bg-secondary border",
                        busy && "opacity-60 cursor-not-allowed",
                    )}
                >
                    <span
                        className={cn(
                            "inline-block h-4 w-4 rounded-full bg-background shadow transition-transform",
                            on ? "translate-x-6" : "translate-x-1",
                        )}
                    />
                </button>
            </div>
        </div>
    );
}

// ── Дата доверия невязкам (SPEC-050) ────────────────────────────────────────

/**
 * Порог, с которого невязки снапшотов считаются осмысленными. До него данные
 * обычно залиты импортом из прежней системы: там расхождение говорит о качестве
 * импорта, а не о том, насколько аккуратно ведётся запись сейчас. Пусто — порога
 * нет, считается вся история (нормальный дефолт для нового инстанса).
 */
function ImbalanceTrustSection() {
    const config = useInstanceConfig();
    const setTrust = useSetImbalanceTrustFrom();
    const saved = config.data?.imbalance_trust_from ?? null;
    const [value, setValue] = useState<string>("");
    const [touched, setTouched] = useState(false);
    const shown = touched ? value : (saved ?? "");
    const busy = setTrust.isPending || config.isLoading;
    const dirty = shown !== (saved ?? "");

    return (
        <div className="rounded-xl border bg-card p-4">
            <div className="flex items-start justify-between gap-6 flex-wrap">
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <Scale className="h-4 w-4 text-muted-foreground shrink-0" />
                        <h2 className="font-medium">Дата доверия невязкам</h2>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl">
                        С этой даты расхождение между снапшотами и записанными потоками считается
                        «неучтёнными потоками» и попадает в сводки. Раньше неё — только в ленту снапшотов,
                        с пометкой: там обычно лежит импорт из прежней системы.
                    </p>
                    <p className="text-xs text-muted-foreground mt-1.5 max-w-2xl">
                        Пусто — порога нет, журнал считается по всей истории. Смена даты пересчитывает журнал.
                    </p>
                    {setTrust.isError && (
                        <p className="text-sm text-destructive mt-2">Не удалось сохранить — попробуй ещё раз.</p>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <input
                        type="date"
                        value={shown}
                        disabled={busy}
                        onChange={e => { setTouched(true); setValue(e.target.value); }}
                        className="px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        aria-label="Дата доверия невязкам"
                    />
                    <button
                        type="button"
                        disabled={busy || !dirty}
                        onClick={() => setTrust.mutate(shown || null, { onSuccess: () => setTouched(false) })}
                        className="btn-primary px-3 py-2 text-sm disabled:opacity-50"
                    >
                        {busy ? "…" : "Сохранить"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── Вёдра ───────────────────────────────────────────────────────────────────

function AccountsSection() {
    const accounts = useAccounts();
    const currencies = useCurrencies();
    const create = useCreateAccount();
    const update = useUpdateAccount();
    const remove = useDeleteAccount();

    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
    const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string; usage: AccountUsage } | null>(null);
    const [error, setError] = useState<string | null>(null);

    const rows = accounts.data?.accounts ?? [];
    const ccyList = currencies.data?.currencies ?? [];

    if (accounts.isError) return <ErrorState onRetry={() => accounts.refetch()} />;

    async function handleDelete(id: string, name: string, force: boolean) {
        setError(null);
        try {
            await remove.mutateAsync({ id, force });
            setPendingDelete(null);
        } catch (e: any) {
            // 409 приходит с разбивкой по таблицам — показываем её и переспрашиваем.
            const usage = e?.body?.usage as AccountUsage | undefined;
            if (usage) setPendingDelete({ id, name, usage });
            else setError(e?.message ?? "не удалось удалить");
        }
    }

    return (
        <div className="space-y-4">
            <div className="flex justify-end">
                <button
                    onClick={() => setOpen(true)}
                    disabled={ccyList.length === 0}
                    className="btn-primary px-4 py-2 disabled:opacity-50"
                    title={ccyList.length === 0 ? "Сначала заведи хотя бы одну валюту" : undefined}
                >
                    <Plus className="h-4 w-4" /> Новое ведро
                </button>
            </div>

            {error && (
                <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm">{error}</div>
            )}

            {rows.length === 0 ? (
                <EmptyState
                    icon={<Wallet className="h-8 w-8" />}
                    title="Вёдер пока нет"
                    hint="Ведро — место, где лежат деньги: «EUR · банк», «RUB · нал». Без вёдер не считаются net worth, снапшоты и свободные деньги."
                    action={ccyList.length === 0 ? "Сначала заведи валюту на соседней вкладке" : undefined}
                />
            ) : (
                <div className="rounded-xl border bg-card divide-y">
                    {rows.map(acc => (
                        <div key={acc.id} className="flex items-center gap-3 px-4 py-3 hover:bg-secondary/40 transition-colors">
                            <span
                                className="h-8 w-8 rounded-lg shrink-0 border"
                                style={acc.color ? { backgroundColor: acc.color } : undefined}
                            />
                            <div className="min-w-0 flex-1">
                                <div className="font-medium truncate">
                                    {acc.name}
                                    {acc.is_investment ? (
                                        <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-600 dark:text-sky-400">
                                            инвест
                                        </span>
                                    ) : null}
                                    {acc.is_active === 0 ? (
                                        <span className="ml-2 text-xs text-muted-foreground">скрыто</span>
                                    ) : null}
                                </div>
                                <div className="text-xs text-muted-foreground font-mono">
                                    {acc.id} · {acc.currency} · {acc.form ? (FORM_LABEL[acc.form] ?? acc.form) : "—"}
                                </div>
                            </div>
                            <button
                                onClick={() => setEditing({ id: acc.id, name: acc.name })}
                                className="p-2 rounded-lg hover:bg-secondary" title="Переименовать"
                            >
                                <Pencil className="h-4 w-4" />
                            </button>
                            <button
                                onClick={() => update.mutate({ id: acc.id, patch: { is_active: acc.is_active === 0 } })}
                                className="p-2 rounded-lg hover:bg-secondary"
                                title={acc.is_active === 0 ? "Показывать" : "Скрыть из выбора"}
                            >
                                {acc.is_active === 0 ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                            </button>
                            <button
                                onClick={() => handleDelete(acc.id, acc.name, false)}
                                className="p-2 rounded-lg hover:bg-destructive/10 text-destructive" title="Удалить"
                            >
                                <Trash2 className="h-4 w-4" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <p className="text-xs text-muted-foreground">
                Валюту и форму существующего ведра сменить нельзя: в них хранится вся история сумм.
                Нужна другая валюта — заведи отдельное ведро.
            </p>

            {open && (
                <AccountModal
                    currencies={ccyList}
                    onClose={() => setOpen(false)}
                    onSubmit={async payload => {
                        setError(null);
                        try {
                            await create.mutateAsync(payload);
                            setOpen(false);
                        } catch (e: any) {
                            setError(e?.body?.error ?? e?.message ?? "не удалось создать");
                        }
                    }}
                />
            )}

            {editing && (
                <RenameModal
                    title="Переименовать ведро"
                    initial={editing.name}
                    onClose={() => setEditing(null)}
                    onSubmit={async name => {
                        await update.mutateAsync({ id: editing.id, patch: { name } });
                        setEditing(null);
                    }}
                />
            )}

            {pendingDelete && (
                <Modal open onClose={() => setPendingDelete(null)} title="Ведро не пустое">
                    <div className="space-y-4">
                        <div className="flex gap-3">
                            <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                            <div className="text-sm space-y-2">
                                <p>
                                    На ведре «{pendingDelete.name}» висят записи. Удаление скроет ведро,
                                    но записи останутся в базе и продолжат влиять на историю.
                                </p>
                                <ul className="text-muted-foreground space-y-0.5">
                                    {pendingDelete.usage.expenses > 0 && <li>трат: {pendingDelete.usage.expenses}</li>}
                                    {pendingDelete.usage.snapshots > 0 && <li>снапшотов: {pendingDelete.usage.snapshots}</li>}
                                    {pendingDelete.usage.incomes > 0 && <li>доходов: {pendingDelete.usage.incomes}</li>}
                                    {pendingDelete.usage.transactions > 0 && <li>обменов: {pendingDelete.usage.transactions}</li>}
                                </ul>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setPendingDelete(null)} className="px-4 py-2 rounded-lg border">Отмена</button>
                            <button
                                onClick={() => handleDelete(pendingDelete.id, pendingDelete.name, true)}
                                className="px-4 py-2 rounded-lg bg-destructive text-destructive-foreground"
                            >
                                Всё равно скрыть
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}

function AccountModal({ currencies, onClose, onSubmit }: {
    currencies: CurrencyRow[];
    onClose: () => void;
    onSubmit: (p: { name: string; currency: string; form: "cash" | "digital"; is_investment: boolean }) => void;
}) {
    const [currency, setCurrency] = useState(currencies[0]?.code ?? "");
    const [form, setForm] = useState<"cash" | "digital">("digital");
    const [isInvestment, setIsInvestment] = useState(false);
    const suggested = useMemo(
        () => (currency ? `${currency} · ${form === "cash" ? "нал" : "банк"}` : ""),
        [currency, form],
    );
    const [name, setName] = useState("");

    return (
        <Modal open onClose={onClose} title="Новое ведро">
            <form
                className="space-y-4"
                onSubmit={e => {
                    e.preventDefault();
                    onSubmit({ name: (name || suggested).trim(), currency, form, is_investment: isInvestment });
                }}
            >
                <label className="block space-y-1.5">
                    <span className="text-sm font-medium">Валюта</span>
                    <select
                        value={currency} onChange={e => setCurrency(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border bg-background text-sm"
                    >
                        {currencies.map(c => (
                            <option key={c.code} value={c.code}>{c.emoji ? `${c.emoji} ` : ""}{c.code} — {c.name}</option>
                        ))}
                    </select>
                </label>

                <div className="space-y-1.5">
                    <span className="text-sm font-medium">Форма</span>
                    <div className="grid grid-cols-2 gap-1 p-1 bg-secondary/60 rounded-lg">
                        {(["digital", "cash"] as const).map(f => (
                            <button
                                key={f} type="button" onClick={() => setForm(f)}
                                className={cn(
                                    "px-3 py-2 rounded-md text-sm transition-colors",
                                    form === f ? "bg-background shadow-sm font-medium" : "text-muted-foreground",
                                )}
                            >
                                {f === "digital" ? "Безнал" : "Наличные"}
                            </button>
                        ))}
                    </div>
                </div>

                <label className="block space-y-1.5">
                    <span className="text-sm font-medium">Название</span>
                    <input
                        value={name} onChange={e => setName(e.target.value)} maxLength={100}
                        placeholder={suggested}
                        className="w-full px-3 py-2 rounded-lg border bg-background text-sm"
                    />
                </label>

                <label className="flex items-start gap-2.5 text-sm">
                    <input
                        type="checkbox" checked={isInvestment} onChange={e => setIsInvestment(e.target.checked)}
                        className="mt-0.5"
                    />
                    <span>
                        Инвестиционное ведро
                        <span className="block text-xs text-muted-foreground">
                            Входит в net worth, но исключается из свободных денег. Одно на валюту.
                        </span>
                    </span>
                </label>

                <div className="flex justify-end gap-2 pt-1">
                    <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border">Отмена</button>
                    <button type="submit" disabled={!currency} className="btn-primary px-4 py-2 disabled:opacity-50">Создать</button>
                </div>
            </form>
        </Modal>
    );
}

// ── Валюты ──────────────────────────────────────────────────────────────────

function CurrenciesSection() {
    const currencies = useCurrencies();
    const create = useCreateCurrency();
    const update = useUpdateCurrency();
    const [open, setOpen] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const rows = currencies.data?.currencies ?? [];
    if (currencies.isError) return <ErrorState onRetry={() => currencies.refetch()} />;

    return (
        <div className="space-y-4">
            <div className="flex justify-end">
                <button onClick={() => setOpen(true)} className="btn-primary px-4 py-2">
                    <Plus className="h-4 w-4" /> Новая валюта
                </button>
            </div>

            {error && <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm">{error}</div>}

            {rows.length === 0 ? (
                <EmptyState
                    icon={<Coins className="h-8 w-8" />}
                    title="Валют пока нет"
                    hint="Заведи хотя бы валюту, которой пользуешься. Курсы к базовой подтянутся сами."
                />
            ) : (
                <div className="rounded-xl border bg-card divide-y">
                    {rows.map(c => (
                        <div key={c.code} className="flex items-center gap-3 px-4 py-3 hover:bg-secondary/40 transition-colors">
                            <span className="text-lg w-7 text-center shrink-0">{c.emoji ?? "•"}</span>
                            <div className="min-w-0 flex-1">
                                <div className="font-medium">
                                    {c.code}
                                    {c.is_crypto ? (
                                        <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-600 dark:text-violet-400">
                                            крипто
                                        </span>
                                    ) : null}
                                </div>
                                <div className="text-xs text-muted-foreground truncate">
                                    {c.name} · {c.decimals} знак{c.decimals === 1 ? "" : c.decimals < 5 ? "а" : "ов"} после запятой
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    const name = window.prompt(`Название для ${c.code}`, c.name);
                                    if (name && name.trim()) update.mutate({ code: c.code, patch: { name: name.trim() } });
                                }}
                                className="p-2 rounded-lg hover:bg-secondary" title="Переименовать"
                            >
                                <Pencil className="h-4 w-4" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <p className="text-xs text-muted-foreground">
                Валюты не удаляются: их коды хранятся в тратах, доходах и курсах — удаление осиротило бы историю.
                Неиспользуемую валюту достаточно не выбирать.
            </p>

            {open && (
                <CurrencyModal
                    onClose={() => setOpen(false)}
                    onSubmit={async payload => {
                        setError(null);
                        try {
                            await create.mutateAsync(payload);
                            setOpen(false);
                        } catch (e: any) {
                            setError(e?.body?.error ?? e?.message ?? "не удалось создать");
                        }
                    }}
                />
            )}
        </div>
    );
}

function CurrencyModal({ onClose, onSubmit }: {
    onClose: () => void;
    onSubmit: (p: { code: string; name: string; emoji: string | null; is_crypto: boolean; decimals: number }) => void;
}) {
    const [code, setCode] = useState("");
    const [name, setName] = useState("");
    const [emoji, setEmoji] = useState("");
    const [isCrypto, setIsCrypto] = useState(false);
    const [decimals, setDecimals] = useState(2);

    return (
        <Modal open onClose={onClose} title="Новая валюта">
            <form
                className="space-y-4"
                onSubmit={e => {
                    e.preventDefault();
                    onSubmit({
                        code: code.trim().toUpperCase(), name: name.trim(),
                        emoji: emoji.trim() || null, is_crypto: isCrypto, decimals,
                    });
                }}
            >
                <div className="grid grid-cols-2 gap-3">
                    <label className="block space-y-1.5">
                        <span className="text-sm font-medium">Код</span>
                        <input
                            value={code} onChange={e => setCode(e.target.value.toUpperCase())}
                            maxLength={10} placeholder="PLN" required
                            className="w-full px-3 py-2 rounded-lg border bg-background text-sm font-mono uppercase"
                        />
                    </label>
                    <label className="block space-y-1.5">
                        <span className="text-sm font-medium">Эмодзи</span>
                        <input
                            value={emoji} onChange={e => setEmoji(e.target.value)} maxLength={4} placeholder="🇵🇱"
                            className="w-full px-3 py-2 rounded-lg border bg-background text-sm"
                        />
                    </label>
                </div>

                <label className="block space-y-1.5">
                    <span className="text-sm font-medium">Название</span>
                    <input
                        value={name} onChange={e => setName(e.target.value)} maxLength={100}
                        placeholder="Польский злотый" required
                        className="w-full px-3 py-2 rounded-lg border bg-background text-sm"
                    />
                </label>

                <div className="grid grid-cols-2 gap-3 items-end">
                    <label className="block space-y-1.5">
                        <span className="text-sm font-medium">Знаков после запятой</span>
                        <input
                            type="number" min={0} max={18} value={decimals}
                            onChange={e => setDecimals(Number(e.target.value))}
                            className="w-full px-3 py-2 rounded-lg border bg-background text-sm"
                        />
                    </label>
                    <label className="flex items-center gap-2 text-sm pb-2">
                        <input type="checkbox" checked={isCrypto} onChange={e => setIsCrypto(e.target.checked)} />
                        Криптовалюта
                    </label>
                </div>

                <div className="flex justify-end gap-2 pt-1">
                    <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border">Отмена</button>
                    <button type="submit" className="btn-primary px-4 py-2">Создать</button>
                </div>
            </form>
        </Modal>
    );
}

// ── Общее ───────────────────────────────────────────────────────────────────

function RenameModal({ title, initial, onClose, onSubmit }: {
    title: string; initial: string; onClose: () => void; onSubmit: (name: string) => void;
}) {
    const [name, setName] = useState(initial);
    return (
        <Modal open onClose={onClose} title={title}>
            <form
                className="space-y-4"
                onSubmit={e => { e.preventDefault(); if (name.trim()) onSubmit(name.trim()); }}
            >
                <input
                    value={name} onChange={e => setName(e.target.value)} maxLength={100} autoFocus
                    className="w-full px-3 py-2 rounded-lg border bg-background text-sm"
                />
                <div className="flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border">Отмена</button>
                    <button type="submit" className="btn-primary px-4 py-2">Сохранить</button>
                </div>
            </form>
        </Modal>
    );
}

function EmptyState({ icon, title, hint, action }: {
    icon: React.ReactNode; title: string; hint: string; action?: string;
}) {
    return (
        <div className="rounded-xl border border-dashed bg-card/50 px-6 py-12 text-center">
            <div className="text-muted-foreground flex justify-center mb-3">{icon}</div>
            <p className="font-medium">{title}</p>
            <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">{hint}</p>
            {action && <p className="text-sm text-muted-foreground mt-2">{action}</p>}
        </div>
    );
}
